import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { VibeUsageTypeReadSchema } from "../../../../shared/domain/vibe/usage-types";
import { VibeCostSafetyStatusReadSchema } from "./status-read.schemas";

/**
 * Org-facing cost-safety read. Where `GetUsage` answers "how much have I
 * consumed?" (quantities only), this answers "how much of my cap have I used,
 * and am I over the soft limit?" — the self-serve counterpart to the admin-only
 * `/api/admin/vibe/cost-safety` + `/api/admin/vibe/consumption-cap` surfaces.
 *
 * Whole-org, current UTC calendar month. Bridges the Prisma
 * `VibeOrgCostSafetyStatus` enum (`z.enum(DbEnum.…_VALUES)`) rather than a
 * hand-kept literal union so a new status can never drift out of sync.
 */
export const VibeCostSafetyStatusSchema = z.enum(DbEnum.VIBE_ORG_COST_SAFETY_STATUS_VALUES);

export type VibeCostSafetyStatusValue = z.infer<typeof VibeCostSafetyStatusSchema>;

/**
 * One usage type's consumption against its effective cap for the current
 * billing month. `currentSum` is the raw consumed quantity (compute-minutes /
 * build-minutes / egress-MB); `effectiveCap` is the org override resolved
 * against the platform default. The console draws the meter from
 * `currentSum / effectiveCap` and flags the over-cap state when it exceeds 1.
 */
export const VibeCostSafetyUsageEntrySchema = z.object({
  usageType: VibeUsageTypeReadSchema,
  /// Consumed quantity in the current UTC calendar month.
  currentSum: z.number().nonnegative(),
  /// Effective per-type cap (org override ?? platform default), a whole count.
  effectiveCap: z.number().int().nonnegative()
});

export type VibeCostSafetyUsageEntryDto = z.infer<typeof VibeCostSafetyUsageEntrySchema>;

/**
 * Cost-safety read response. `status` is the org's current cost-safety state
 * (OK / WARNING / SUSPENDED — the row's absence reads as OK). `usage` carries
 * one entry per `VibeUsageType`, always all types even at zero, so the console
 * renders a stable, exhaustive set of meters.
 */
export const GetVibeCostSafetyResponseSchema = z.object({
  status: VibeCostSafetyStatusReadSchema,
  /// UTC calendar month the sums + caps apply to, `YYYY-MM`.
  billingPeriod: z.string().regex(/^\d{4}-\d{2}$/, "billingPeriod must be YYYY-MM"),
  usage: z.array(VibeCostSafetyUsageEntrySchema)
});

export type GetVibeCostSafetyResponse = z.infer<typeof GetVibeCostSafetyResponseSchema>;
